import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';

const DEFAULT_PHOTO =
  'https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png';

/**
 * CONFIRM ACTION — the last look before money moves.
 *
 * Every money action in the merchant panel opens this first: what is about to
 * happen, to whom, how much, the balance before and after, and a plain note on
 * the real effect. Cancel / Confirm.
 *
 * `onConfirm` may return a promise. While it runs Confirm is disabled and a
 * second click is ignored (a ref, not state, so two clicks in the same tick
 * cannot both fire). Closing on success is the caller's job — it sets `show`
 * back to false once the request went through, and leaves it open on failure
 * so the merchant can read the toast and try again or cancel.
 *
 * Props
 *   show, onCancel, onConfirm
 *   title        — the action name, e.g. "Approve withdraw"
 *   recipient    — { name, username, photo } (optional)
 *   amount       — the headline figure, already formatted, e.g. "Tk 1,200"
 *   amountHint   — small line under it, e.g. "100 pt × 12"
 *   rows         — [{ label, value, strong, tone: 'good' | 'bad' }]
 *   note         — what actually happens when confirmed
 *   children     — anything extra (e.g. a member list for a bulk action)
 *   confirmLabel — defaults to "Confirm"
 *   busy         — the caller's own in-flight flag, ORed with ours
 *   danger       — red Confirm, for anything that cannot be reversed badly
 */
export default function ConfirmActionModal({
  show,
  onCancel,
  onConfirm,
  title,
  recipient,
  amount,
  amountHint,
  rows = [],
  note,
  children,
  confirmLabel = 'Confirm',
  busyLabel = 'Working…',
  busy = false,
  danger = false,
}) {
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  // Re-arm every time the modal opens.
  useEffect(() => {
    if (show) {
      inFlight.current = false;
      setSubmitting(false);
    }
  }, [show]);

  const working = busy || submitting;

  const confirm = async () => {
    if (inFlight.current || working) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      await onConfirm?.();
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const cancel = () => {
    if (working) return;
    onCancel?.();
  };

  return (
    <Modal
      show={show}
      onHide={cancel}
      centered
      backdrop={working ? 'static' : true}
      keyboard={!working}
      dialogClassName="cam-dialog"
    >
      <style>{CSS}</style>
      <Modal.Header closeButton={!working}>
        <Modal.Title className="cam-title">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="cam-body">
        {recipient ? (
          <div className="cam-who">
            <img src={recipient.photo || DEFAULT_PHOTO} alt="" className="cam-avatar" />
            <div className="cam-who-text">
              <b>{recipient.name || recipient.username}</b>
              {recipient.username ? <small>@{recipient.username}</small> : null}
            </div>
          </div>
        ) : null}

        {amount ? (
          <div className={danger ? 'cam-amount cam-amount-danger' : 'cam-amount'}>
            <strong>{amount}</strong>
            {amountHint ? <small>{amountHint}</small> : null}
          </div>
        ) : null}

        {rows.length ? (
          <div className="cam-rows">
            {rows.map((r, i) => (
              <div
                key={i}
                className={`cam-row${r.strong ? ' cam-row-strong' : ''}${r.tone ? ` cam-${r.tone}` : ''}`}
              >
                <span>{r.label}</span>
                <b>{r.value}</b>
              </div>
            ))}
          </div>
        ) : null}

        {children}

        {note ? (
          <div className="cam-note">
            <i className="fa fa-info-circle" aria-hidden="true"></i>
            <span>{note}</span>
          </div>
        ) : null}
        <div className="cam-warn">
          <i className="fa fa-shield" aria-hidden="true"></i>
          <span>Check these details carefully. This cannot be undone once confirmed.</span>
        </div>
      </Modal.Body>
      <Modal.Footer className="cam-footer">
        <button type="button" className="cam-btn cam-btn-light" onClick={cancel} disabled={working}>
          Cancel
        </button>
        <button
          type="button"
          className={danger ? 'cam-btn cam-btn-danger' : 'cam-btn cam-btn-go'}
          onClick={confirm}
          disabled={working}
        >
          {working ? busyLabel : confirmLabel}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

const CSS = `
.cam-dialog { max-width: 460px; }
@media (max-width: 575px) { .cam-dialog { margin: 12px; } }
.cam-title { font-size: 16px; font-weight: 800; color: #14506e; }
.cam-body { display: flex; flex-direction: column; gap: 10px; }
.cam-who { display: flex; align-items: center; gap: 10px; }
.cam-avatar { width: 44px; height: 44px; border-radius: 50%; object-fit: cover; background: #f1f5f9;
  flex-shrink: 0; }
.cam-who-text { display: flex; flex-direction: column; min-width: 0; line-height: 1.25; }
.cam-who-text b { font-size: 15px; color: #111827; overflow-wrap: anywhere; }
.cam-who-text small { font-size: 12px; color: #6b7280; overflow-wrap: anywhere; }
.cam-amount { text-align: center; background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 12px;
  padding: 10px; display: flex; flex-direction: column; }
.cam-amount strong { font-size: 26px; font-weight: 800; color: #166534; }
.cam-amount small { font-size: 12px; color: #6b7280; }
.cam-amount-danger { background: #fef2f2; border-color: #fecaca; }
.cam-amount-danger strong { color: #991b1b; }
.cam-rows { background: #f8fafc; border-radius: 12px; padding: 8px 12px; }
.cam-row { display: flex; justify-content: space-between; gap: 12px; padding: 5px 0; font-size: 13px; }
.cam-row span { color: #6b7280; }
.cam-row b { color: #111827; text-align: right; overflow-wrap: anywhere; }
.cam-row-strong { border-top: 1px solid #e5e7eb; margin-top: 3px; padding-top: 8px; }
.cam-row-strong b { font-size: 15px; }
.cam-good b { color: #166534; }
.cam-bad b { color: #b91c1c; }
.cam-note, .cam-warn { display: flex; gap: 8px; border-radius: 10px; padding: 9px 11px;
  font-size: 12.5px; line-height: 1.45; }
.cam-note { background: #e6f5fd; border: 1px solid #bfe4f8; color: #14506e; }
.cam-warn { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; }
.cam-note i, .cam-warn i { margin-top: 2px; }
.cam-footer { gap: 8px; flex-wrap: nowrap; }
.cam-btn { flex: 1; border-radius: 10px; padding: 10px 14px; font-weight: 700; font-size: 14px;
  cursor: pointer; border: 1px solid #cfd8e3; background: #fff; }
.cam-btn:disabled { opacity: .6; cursor: not-allowed; }
.cam-btn-light { background: #f1f5f9; }
.cam-btn-go { background: #16a34a; border-color: #16a34a; color: #fff; }
.cam-btn-go:hover:enabled { background: #15803d; }
.cam-btn-danger { background: #dc2626; border-color: #dc2626; color: #fff; }
.cam-list { max-height: 150px; overflow-y: auto; border: 1px solid #e5e7eb; border-radius: 10px;
  padding: 4px 10px; font-size: 12.5px; }
.cam-list > div { display: flex; justify-content: space-between; gap: 8px; padding: 4px 0;
  border-bottom: 1px dashed #f1f5f9; }
.cam-list > div:last-child { border-bottom: 0; }
`;
